import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, CheckCircle2, Sparkles, Layers } from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import QuoteDialog from "../components/QuoteDialog";
import { fetchProjects } from "../lib/api";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchProjects()
      .then((data) => setProjects(data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  const openQuote = (p) => {
    setActive(p);
    setOpen(true);
  };

  const featured = projects.find((p) => p.is_featured) || projects[0];
  const others = projects.filter((p) => p !== featured);

  return (
    <div data-testid="projects-page" className="bg-[#050505] text-white min-h-screen">
      <section className="pt-32 pb-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Our Projects</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter mt-6 leading-[1.05] max-w-4xl">
            Products engineered by Operexa.<br />
            <span className="text-[#3B82F6]">Built for scale.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            A growing portfolio of in-house digital products and platforms. Request a quote and we'll provide a live demo tailored to your goals.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="py-32 text-center text-zinc-500">Loading projects…</div>
      ) : (
        <>
          {/* FEATURED PROJECT */}
          {featured && (
            <section className="py-20">
              <div className="max-w-7xl mx-auto px-6 md:px-10">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  data-testid={`project-card-${featured.slug}`}
                  className="border border-white/10 bg-[#0a0a0c] grid grid-cols-1 lg:grid-cols-12 overflow-hidden"
                >
                  <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto">
                    <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
                    <div className="absolute top-6 left-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/60 backdrop-blur border border-white/20">
                        <Sparkles className="w-3.5 h-3.5 text-[#3B82F6]" />
                        <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Featured Project</span>
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col">
                    <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-3">
                      {featured.slug}
                    </div>
                    <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-3">{featured.title}</h2>
                    <p className="text-zinc-300 mb-5">{featured.tagline}</p>
                    <p className="text-zinc-500 text-sm leading-relaxed mb-6">{featured.description}</p>

                    <div className="grid grid-cols-2 gap-2 mb-8">
                      {featured.features.slice(0, 6).map((f) => (
                        <div key={f} className="flex items-start gap-2 text-xs text-zinc-400">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#3B82F6] mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto">
                      <div className="flex items-baseline gap-2 mb-5 pb-5 border-b border-white/10">
                        <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Pricing</span>
                        <span className="font-display text-2xl font-bold text-white">{featured.pricing_label}</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          data-testid={`quote-btn-${featured.slug}`}
                          onClick={() => openQuote(featured)}
                          className="group inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white font-semibold hover:bg-[#60A5FA] transition-colors"
                        >
                          Request Quote
                          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        {featured.demo_url && (
                          <a
                            href={featured.demo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`demo-link-${featured.slug}`}
                            className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white hover:border-white hover:bg-white/5 transition-colors"
                          >
                            View Live Demo <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 mt-4">A live demo will also be provided on request.</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* OTHER PROJECTS */}
          <section className="py-20 border-t border-white/10 bg-[#08080a]">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <SectionLabel>More Projects</SectionLabel>
              <h2 className="font-display text-2xl lg:text-4xl font-semibold tracking-tight mt-4 max-w-3xl">
                The portfolio is growing.
              </h2>
              <p className="text-zinc-400 mt-3 max-w-2xl">
                We are continuously adding new platforms and projects. The space below will showcase the full lineup as we ship.
              </p>

              {others.length > 0 ? (
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {others.map((p) => (
                    <div key={p.slug} className="border border-white/10 bg-[#0a0a0c] overflow-hidden" data-testid={`project-card-${p.slug}`}>
                      <div className="aspect-video overflow-hidden">
                        <img src={p.image_url} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                      </div>
                      <div className="p-6">
                        <h3 className="font-display text-xl font-semibold mb-2">{p.title}</h3>
                        <p className="text-sm text-zinc-400 mb-4 line-clamp-3">{p.tagline}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-white/10">
                          <span className="text-sm font-medium text-white">{p.pricing_label}</span>
                          <button
                            data-testid={`quote-btn-${p.slug}`}
                            onClick={() => openQuote(p)}
                            className="text-sm font-semibold text-[#3B82F6] hover:text-[#60A5FA] inline-flex items-center gap-1"
                          >
                            Request Quote <ArrowUpRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="border border-dashed border-white/10 bg-[#0a0a0c] p-10 text-center" data-testid={`coming-soon-${i}`}>
                      <Layers className="w-6 h-6 text-zinc-700 mx-auto mb-3" />
                      <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">Coming Soon</div>
                      <p className="text-sm text-zinc-500 mt-2">A new project is on the way.</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      <QuoteDialog
        open={open}
        onOpenChange={setOpen}
        projectSlug={active?.slug || ""}
        projectTitle={active?.title || ""}
      />
    </div>
  );
}
